"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { MessageCircle, Loader2, ChevronDown } from "lucide-react";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="59554" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="rounded-full bg-primary/10 p-3 text-primary">
          <MessageCircle className="size-8" />
        </div>
        <h3 className="text-xl font-bold text-foreground font-sans">Tack för din ansökan!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto font-sans">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-border text-foreground hover:bg-accent hover:text-foreground rounded-sm font-sans">
          Skicka en ny anmälan
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Vänligen ange kontaktperson"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-sans">Kontaktperson</FormLabel>
            <FormControl>
              <input {...field} type="text" placeholder="Anna Andersson" className="flex h-11 w-full rounded-sm border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors font-sans" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "E-postadress krävs",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Ogiltig e-postadress"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-sans">E-postadress</FormLabel>
            <FormControl>
              <input {...field} type="email" placeholder="anna@foretag.se" className="flex h-11 w-full rounded-sm border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors font-sans" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-sans">Telefonnummer (valfritt)</FormLabel>
            <FormControl>
              <input {...field} type="tel" placeholder="+46 70 123 45 67" className="flex h-11 w-full rounded-sm border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors font-sans" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subject" rules={{
      required: "Vänligen välj en region"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-sans">Stad / Region</FormLabel>
            <FormControl>
              <div className="relative">
                <select {...field} className="flex h-11 w-full appearance-none rounded-sm border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors font-sans">
                  <option value="" disabled>Välj stad...</option>
                  <option value="Stockholm">Stockholm</option>
                  <option value="Göteborg">Göteborg</option>
                  <option value="Malmö">Malmö</option>
                  <option value="Uppsala">Uppsala</option>
                  <option value="Annan">Annan region</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground">
                  <ChevronDown className="size-4" />
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Information om verksamheten krävs"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-sans">Beskrivning av verksamhet</FormLabel>
            <FormControl>
              <textarea {...field} rows={5} placeholder="Berätta kort om din flotta och nuvarande verksamhet..." className="flex w-full rounded-sm border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors resize-none font-sans" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm font-sans">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold h-12 mt-4 shadow-sm hover:shadow-md transition-all rounded-sm font-sans">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Skickar...
          </> : "Skicka ansökan"}
      </Button>
    </Form>;
}
export default function SupportOchKontakt() {
  return <section id="operatörsregistrering" className="bg-background text-foreground py-8 flex justify-center">
      <SafeHydrate><Drawer>
        <DrawerTrigger asChild>
          <Button variant="link" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 text-sm font-bold font-sans">
            <MessageCircle className="size-4" />
            Bli Operatör
          </Button>
        </DrawerTrigger>
        <DrawerContent className="bg-background border-t border-border max-h-[90vh]">
          <div className="mx-auto w-full max-w-[500px]">
            <DrawerHeader className="text-center pt-8 pb-4">
              <DrawerTitle className="text-2xl font-bold text-foreground tracking-tight font-sans">Operatörsregistrering</DrawerTitle>
              <DrawerDescription className="text-muted-foreground font-sans">
                Registrera din flotta för att ansluta dig till framtidens transportnätverk och robotaxi-tjänster.
              </DrawerDescription>
            </DrawerHeader>
            
            <div className="px-6 pb-8 overflow-y-auto max-h-[70vh]">
              <FormProvider formKey="operator_registration_form_robotaxioperatrregistrering" formId="operator_registration_form_robotaxioperatrregistrering" sectionName="support-contact" defaultValues={{
                name: "",
                email: "",
                phone: "",
                subject: "Stockholm",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                subject: {
                  required: true
                },
                message: {
                  required: true
                }
              }} submitText="Skicka ansökan" successMessage="Tack! Vi har tagit emot din ansökan och återkommer till dig inom 48 timmar.">
                <ContactForm />
              </FormProvider>
            </div>

            <DrawerFooter className="pt-2 pb-8">
              <DrawerClose asChild>
                <Button variant="ghost" className="text-muted-foreground hover:text-foreground hover:bg-accent rounded-sm font-sans">
                  Avbryt
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer></SafeHydrate>
    </section>;
}